// src/components/RegCompanyStep.js
// Step 1 — company by Registre de Commerce (RC).
//
// Smart behaviour:
//  - Lookup by RC. If the company EXISTS: prefill, and LOCK every field except
//    Téléphone (and Email, conditionally). Locked fields render read-only.
//  - Email:
//      * if the company email was already verified AND the user keeps it -> no
//        new OTP is required (auto-verified).
//      * if the user CHANGES the email -> a fresh OTP is required.
//      * brand-new company -> OTP required.
//  - If the company is ALREADY registered for THIS event, we surface that to the
//    parent (onExistingRegistration) so it can show the participant list with
//    add / remove / cancel.
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import eventApi from "../config/eventApi";

export default function RegCompanyStep({
  idEvent, company, setCompany, otpVerified, setOtpVerified, onNext,
  onExistingRegistration, requireOtp = true,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [looking, setLooking] = useState(false);
  const [existing, setExisting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  const [emailPreVerified, setEmailPreVerified] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Reference lists (sectors + wilayas)
  const [secteurs, setSecteurs] = useState([]);
  const [wilayas, setWilayas] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [sRes, wRes] = await Promise.all([
          eventApi.refSecteurs(lang),
          eventApi.refWilayas(lang),
        ]);
        if (active) {
          setSecteurs(Array.isArray(sRes.data) ? sRes.data : []);
          setWilayas(Array.isArray(wRes.data) ? wRes.data : []);
        }
      } catch (e) { /* selects just stay empty */ }
    })();
    return () => { active = false; };
  }, [lang]);

  // When the organizer disabled OTP, treat the e-mail as auto-verified so the
  // company step never blocks on a code.
  useEffect(() => {
    if (!requireOtp) setOtpVerified(true);
  }, [requireOtp, setOtpVerified]);

  const set = (k, v) => setCompany((prev) => ({ ...prev, [k]: v }));

  const handleLookup = async () => {
    if (!company.rc) {
      toast.warning(t("reg.rc_required", "Veuillez saisir le N° de Registre de Commerce"));
      return;
    }
    setLooking(true);
    try {
      const { data } = await eventApi.lookupCompany(company.rc, lang, idEvent);
      if (data.exists) {
        setCompany((prev) => ({ ...prev, ...data.company }));
        setExisting(true);
        setLocked(true);
        setOriginalEmail((data.company.email || "").toLowerCase());

        if (data.email_verified) {
          setEmailPreVerified(true);
          setOtpVerified(true);
          toast.success(t("reg.company_found_verified", "Entreprise trouvée — e-mail déjà vérifié"));
        } else {
          setEmailPreVerified(false);
          setOtpVerified(false);
          toast.info(t("reg.company_found", "Entreprise trouvée — informations préremplies"));
        }

        if (data.registration && typeof onExistingRegistration === "function") {
          onExistingRegistration(data.registration, data.company);
        }
      } else {
        setExisting(false);
        setLocked(false);
        setEmailPreVerified(false);
        setOtpVerified(false);
        toast.info(t("reg.company_new", "Nouvelle entreprise — veuillez compléter les informations"));
      }
    } catch (e) {
      toast.error(t("reg.lookup_failed", "Échec de la recherche"));
    } finally {
      setLooking(false);
    }
  };

  const emailChanged = existing && emailPreVerified &&
    (company.email || "").toLowerCase() !== originalEmail;

  const onEmailChange = (v) => {
    set("email", v);
    if (!requireOtp) {
      setOtpVerified(true);
      return;
    }
    if (emailPreVerified) {
      const changed = v.toLowerCase() !== originalEmail;
      setOtpVerified(!changed);
      if (changed) { setOtpSent(false); setOtpCode(""); }
    } else {
      setOtpVerified(false);
      setOtpSent(false);
    }
  };

  const handleSendOtp = async () => {
    if (!company.email) {
      toast.warning(t("reg.email_required", "L'email de l'entreprise est obligatoire"));
      return;
    }
    setSending(true);
    try {
      await eventApi.sendOtp({ email: company.email, id_event: idEvent, rc: company.rc }, lang);
      setOtpSent(true);
      toast.success(t("reg.otp_sent", "Code de vérification envoyé à votre email"));
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.otp_send_failed", "Échec de l'envoi du code"));
    } finally { setSending(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) return;
    setVerifying(true);
    try {
      await eventApi.verifyOtp({ email: company.email, code: otpCode }, lang);
      setOtpVerified(true);
      setEmailPreVerified(true);
      setOriginalEmail((company.email || "").toLowerCase());
      toast.success(t("reg.email_verified", "Email vérifié avec succès"));
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.otp_invalid", "Code incorrect"));
    } finally { setVerifying(false); }
  };

  const canContinue = company.rc && company.raison_sociale && company.email && (otpVerified || !requireOtp);

  const lockedField = (label, value) => (
    <div className="col-md-4">
      <label className="form-label">{label}</label>
      <div className="reg-locked-field">{value || "—"} <i className="bi bi-lock-fill"></i></div>
    </div>
  );

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-white">
        <h5 className="mb-0">
          <i className="bi bi-building me-2 text-primary"></i>
          {t("reg.company_step", "Informations de l'entreprise")}
        </h5>
      </div>
      <div className="card-body">
        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-8">
            <label className="form-label">
              <span className="text-danger">*</span> {t("reg.rc", "N° Registre de Commerce (RC)")}
            </label>
            <input type="text" className="form-control" value={company.rc || ""}
              onChange={(e) => set("rc", e.target.value)}
              placeholder={t("reg.rc_ph", "Ex : 16/00-1234567 B 21")} disabled={locked} />
          </div>
          <div className="col-md-4">
            <button type="button" className="btn btn-outline-primary w-100" onClick={handleLookup} disabled={looking || locked}>
              {looking
                ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.checking", "Vérification...")}</>
                : <><i className="bi bi-search me-1"></i>{t("reg.check", "Vérifier")}</>}
            </button>
          </div>
        </div>

        {existing && (
          <div className="alert alert-info py-2 d-flex justify-content-between align-items-center flex-wrap">
            <span>
              <i className="bi bi-info-circle me-1"></i>
              {t("reg.company_locked_note", "Entreprise déjà enregistrée : ses informations sont verrouillées. Vous pouvez modifier le téléphone (et l'e-mail si nécessaire).")}
            </span>
          </div>
        )}

        <div className="row g-3">
          {locked ? (
            <>
              {lockedField(t("reg.raison_sociale", "Raison sociale"), company.raison_sociale)}
              {lockedField(t("reg.nif", "NIF"), company.nif)}
              {lockedField(t("reg.nis", "NIS"), company.nis)}
              {lockedField(t("reg.article", "Article"), company.article)}
              {lockedField(t("reg.secteur", "Secteur d'activité"),
                (secteurs.find((s) => String(s.id) === String(company.id_secteur)) || {}).label || company.secteur_activite)}
              {lockedField(t("reg.wilaya", "Wilaya"),
                (() => {
                  const w = wilayas.find((x) => x.code === company.wilaya_code);
                  return w ? `${w.code} - ${w.label}` : company.wilaya;
                })())}
              {lockedField(t("reg.ville", "Ville"), company.ville)}
              <div className="col-md-4">
                <label className="form-label">{t("reg.telephone", "Téléphone")}</label>
                <input type="text" className="form-control" value={company.telephone || ""}
                  onChange={(e) => set("telephone", e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="col-md-6">
                <label className="form-label"><span className="text-danger">*</span> {t("reg.raison_sociale", "Raison sociale")}</label>
                <input type="text" className="form-control" value={company.raison_sociale || ""}
                  onChange={(e) => set("raison_sociale", e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("reg.raison_sociale_ar", "Raison sociale (Arabe)")}</label>
                <input type="text" dir="rtl" className="form-control" value={company.raison_sociale_ar || ""}
                  onChange={(e) => set("raison_sociale_ar", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t("reg.nif", "NIF")}</label>
                <input type="text" className="form-control" value={company.nif || ""} onChange={(e) => set("nif", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t("reg.nis", "NIS")}</label>
                <input type="text" className="form-control" value={company.nis || ""} onChange={(e) => set("nis", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{t("reg.article", "Article")}</label>
                <input type="text" className="form-control" value={company.article || ""} onChange={(e) => set("article", e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("reg.telephone", "Téléphone")}</label>
                <input type="text" className="form-control" value={company.telephone || ""} onChange={(e) => set("telephone", e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("reg.secteur", "Secteur d'activité")}</label>
                <select className="form-select" value={company.id_secteur || ""} onChange={(e) => set("id_secteur", e.target.value)}>
                  <option value="">{t("reg.select", "Sélectionner...")}</option>
                  {secteurs.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("reg.wilaya", "Wilaya")}</label>
                <select className="form-select" value={company.wilaya_code || ""}
                  onChange={(e) => {
                    const w = wilayas.find((x) => x.code === e.target.value);
                    set("wilaya_code", e.target.value);
                    if (w) set("wilaya", w.name);
                  }}>
                  <option value="">{t("reg.select", "Sélectionner...")}</option>
                  {wilayas.map((w) => (
                    <option key={w.code} value={w.code}>{w.code} - {w.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("reg.ville", "Ville / Commune")}</label>
                <input type="text" className="form-control" value={company.ville || ""} onChange={(e) => set("ville", e.target.value)} />
              </div>
            </>
          )}

          <div className="col-12"><hr /></div>
          <div className="col-md-8">
            <label className="form-label">
              <span className="text-danger">*</span> {t("reg.company_email", "Email de l'entreprise")}
              {emailPreVerified && !emailChanged && (
                <span className="badge bg-success ms-2"><i className="bi bi-check-circle me-1"></i>{t("reg.verified", "Vérifié")}</span>
              )}
            </label>
            <input
              type="email"
              className={`form-control ${otpVerified ? "is-valid" : ""}`}
              value={company.email || ""}
              onChange={(e) => onEmailChange(e.target.value)}
            />
            {existing && (
              <small className="text-muted">
                {t("reg.email_change_hint", "Vous pouvez conserver cet e-mail (déjà vérifié) ou le modifier — un nouveau code sera alors requis.")}
              </small>
            )}
          </div>
          <div className="col-md-4 d-flex align-items-end">
            {!requireOtp ? (
              <div className="text-muted small"><i className="bi bi-info-circle me-1"></i>{t("reg.no_otp", "Vérification non requise")}</div>
            ) : otpVerified ? (
              <div className="text-success fw-semibold"><i className="bi bi-check-circle-fill me-1"></i>{t("reg.verified", "Vérifié")}</div>
            ) : (
              <button type="button" className="btn btn-warning w-100" onClick={handleSendOtp} disabled={sending}>
                {sending
                  ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.sending", "Envoi...")}</>
                  : <><i className="bi bi-shield-lock me-1"></i>{otpSent ? t("reg.resend_otp", "Renvoyer le code") : t("reg.send_otp", "Envoyer le code")}</>}
              </button>
            )}
          </div>

          {requireOtp && otpSent && !otpVerified && (
            <div className="col-12">
              <div className="alert alert-light border d-flex flex-wrap align-items-end gap-2 mb-0">
                <div className="flex-grow-1">
                  <label className="form-label mb-1">{t("reg.enter_otp", "Saisissez le code reçu par email")}</label>
                  <input type="text" inputMode="numeric" maxLength={6}
                    className="form-control form-control-lg text-center" style={{ letterSpacing: "0.5em", maxWidth: 220 }}
                    value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
                </div>
                <button type="button" className="btn btn-success btn-lg" onClick={handleVerifyOtp} disabled={verifying || otpCode.length < 6}>
                  {verifying
                    ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.verifying", "Vérification...")}</>
                    : <><i className="bi bi-check2 me-1"></i>{t("reg.verify", "Valider")}</>}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-end mt-4">
          <button type="button" className="btn btn-primary btn-lg px-4" onClick={onNext} disabled={!canContinue}>
            {t("reg.continue", "Continuer")} <i className="bi bi-arrow-right ms-1"></i>
          </button>
        </div>
      </div>
    </div>
  );
}